// Revisão espaçada SM-2 (F3) — painel de revisões vencidas na Home.
// Responder alimenta a dimensão APRENDIZADO do Performance Score.

import { todayLocalIso } from '@core/dates';
import { type ReviewQuality, daysOverdue, dueReviews } from '@core/sm2';
import { reviewActions } from '@core/store';
import { toast } from '@ui/components/Toast';
import { useStore } from '@ui/lib/useStore';

const QUALITY_BUTTONS: { q: ReviewQuality; label: string; color: string }[] = [
  { q: 2, label: 'ERREI', color: 'var(--red)' },
  { q: 3, label: 'DIFÍCIL', color: 'var(--amber)' },
  { q: 4, label: 'BOM', color: 'var(--cyan)' },
  { q: 5, label: 'FÁCIL', color: 'var(--green)' },
];

export function ReviewPanel() {
  const contents = useStore((s) => s.contents);
  const todayIso = todayLocalIso();
  const due = dueReviews(contents, todayIso);

  if (due.length === 0) return null;

  const shown = due.slice(0, 5);

  function answer(contentId: string, title: string, q: ReviewQuality) {
    reviewActions.answer(contentId, q);
    const msgs: Record<ReviewQuality, string> = {
      2: `"${title}" volta amanhã — errar faz parte`,
      3: `"${title}" agendada de novo em breve`,
      4: `✓ "${title}" revisada`,
      5: `✓ "${title}" dominada — próxima revisão mais longe`,
    };
    toast(msgs[q], q >= 4 ? 'ok' : 'info', 3000);
  }

  return (
    <div class="panel" style={{ marginBottom: '20px', borderColor: 'rgba(177,74,255,0.35)' }}>
      <div class="panel-title">
        ↻ <b>REVISÃO ESPAÇADA</b> · {due.length} vencida{due.length > 1 ? 's' : ''}
        <span class="tag purple" style={{ fontSize: '9px' }}>
          ALIMENTA O SCORE
        </span>
      </div>
      <div
        style={{
          fontFamily: 'var(--f-mono)',
          fontSize: '10.5px',
          color: 'var(--text-dim)',
          marginBottom: '12px',
          lineHeight: 1.6,
        }}
      >
        Bater o olho no tópico e se perguntar: "eu lembro disso?" — responder honesto ajusta o
        intervalo da próxima revisão (SM-2).
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
        {shown.map((c) => {
          const overdue = c.review ? daysOverdue(c.review, todayIso) : 0;
          return (
            <div
              key={c.id}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '10px',
                flexWrap: 'wrap',
                padding: '10px 12px',
                background: 'rgba(0,8,24,0.4)',
                border: '1px solid var(--border-soft)',
                borderRadius: 'var(--r-sm)',
              }}
            >
              <span
                style={{ flex: 1, fontSize: '13.5px', color: 'var(--text)', minWidth: '140px' }}
              >
                {c.title}
                {overdue > 0 && (
                  <span class="tag amber" style={{ marginLeft: '8px', fontSize: '9px' }}>
                    {overdue}d atraso
                  </span>
                )}
              </span>
              <div style={{ display: 'flex', gap: '6px' }}>
                {QUALITY_BUTTONS.map((b) => (
                  <button
                    key={b.q}
                    type="button"
                    class="btn sm ghost"
                    style={{ color: b.color, borderColor: 'var(--border-soft)' }}
                    onClick={() => answer(c.id, c.title, b.q)}
                  >
                    {b.label}
                  </button>
                ))}
              </div>
            </div>
          );
        })}
        {due.length > shown.length && (
          <div
            style={{
              fontFamily: 'var(--f-mono)',
              fontSize: '10px',
              color: 'var(--text-dim)',
              textAlign: 'center',
            }}
          >
            +{due.length - shown.length} na fila — aparecem conforme você responde
          </div>
        )}
      </div>
    </div>
  );
}
